import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Recycle, FlaskConical, ShieldCheck } from 'lucide-react';
import { useContent } from '../lib/ContentContext';
import { assetUrl } from '../lib/api';
import { PageHero } from '../components/site/PageHero';
import { SectionHeading } from '../components/site/SectionHeading';

const ICONS = { Droplets, Recycle, FlaskConical, ShieldCheck };

const getEmbedUrl = (url) => {
  if (!url) return '';
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
};

export default function RDSustainability() {
  const { content } = useContent();
  const page = content?.rd_page;
  if (!page) return null;

  return (
    <div>
      <PageHero title={page.hero_title} subtitle={page.hero_subtitle} image={page.hero_image} />

      {/* Intro */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <h2 className="text-[#2E8B57] font-extrabold text-3xl sm:text-4xl tracking-tight">{page.intro_title}</h2>
              <div className="mt-6 space-y-4">
                {(page.intro_paragraphs || []).map((p, i) => (
                  <p key={i} className="cms-html text-black/80 leading-8" dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
            <div>
              {page.video_url ? (
                <div className="aspect-video rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(17,24,39,0.12)]">
                  <iframe
                    src={getEmbedUrl(page.video_url)}
                    title="Intexso R&D video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <img
                  src={assetUrl(page.intro_image)}
                  alt="Intexso R&D"
                  className="w-full rounded-2xl shadow-[0_10px_30px_rgba(17,24,39,0.12)] object-cover"
                  loading="lazy"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities: R&D + Application Centre two columns */}
      <section className="py-14 sm:py-20 bg-[#F6F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={page.capabilities_subtitle}>{page.capabilities_title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-stretch">
            {(page.columns || []).map((col, i) => (
              <div key={i} data-testid="rd-capability-column" className="bg-white rounded-2xl shadow-[0_8px_20px_rgba(17,24,39,0.1)] overflow-hidden flex flex-col">
                <img src={assetUrl(col.image)} alt={col.title} className="w-full h-[260px] object-cover" loading="lazy" />
                <div className="p-7 sm:p-8 flex-1 flex flex-col">
                  <h3 className="text-[#26247B] font-extrabold text-2xl">{col.title}</h3>
                  {col.tagline && <p className="mt-1.5 italic font-bold text-[#2E8B57]">{col.tagline}</p>}
                  <div className="mt-4 space-y-3">
                    {(col.paragraphs || []).map((p, j) => (
                      <p key={j} className="text-black/80 text-sm leading-7">{p}</p>
                    ))}
                  </div>
                  {col.list_title && <p className="mt-5 font-bold text-[#26247B]">{col.list_title}</p>}
                  <ul className="mt-3 space-y-2.5">
                    {(col.items || []).map((item, j) => (
                      <li key={j} className="flex gap-2.5 text-sm leading-6">
                        <span className="mt-2 h-2 w-2 rounded-full bg-[#F4A300] shrink-0" />
                        <span className="text-black/80">
                          <strong className="text-[#26247B]">{item.title}:</strong> {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {col.footer && (
                    <p className="mt-5 pt-4 border-t border-[#E6E8EE] text-sm leading-7 font-semibold text-[#2E8B57]">{col.footer}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeading subtitle={page.sustainability_subtitle}>{page.sustainability_title}</SectionHeading>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {(page.sustainability_items || []).map((item, i) => {
              const Icon = ICONS[item.icon] || Droplets;
              return (
                <div key={i} className="flex gap-5 bg-[#F6F7F9] rounded-2xl p-7">
                  <div className="h-14 w-14 rounded-xl bg-[#2E8B57] flex items-center justify-center shrink-0">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[#26247B] font-extrabold leading-snug">{item.title}</h3>
                    <p className="mt-2 text-black/80 text-sm leading-6">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-16 bg-[#26247B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-white font-extrabold text-2xl sm:text-3xl">{page.cta_title}</h2>
          <p className="mt-4 text-white/90 leading-7">{page.cta_text}</p>
          <Link
            to="/contact-us"
            data-testid="rd-cta-button"
            className="mt-7 inline-flex rounded-md bg-[#F4A300] text-white font-bold px-8 py-3.5 transition-colors duration-200 hover:bg-[#d98f00]"
          >
            {page.cta_button}
          </Link>
        </div>
      </section>
    </div>
  );
}
